import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ingressCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { CodeBlock } from '@/ui/CodeBlock/CodeBlock'
import { Disclosure } from '@/ui/Disclosure/Disclosure'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { ApiError } from '../../api/client'
import { ingressApi } from '../../api/ingress'
import { Fact, Facts } from '../Facts'

interface Props {
  workspaceId: string
  resourceId: string
}

/** A path-only endpoint resolves against the browser's origin. */
const absolute = (url: string) =>
  url.startsWith('/') ? `${window.location.origin}${url}` : url

/**
 * Where senders deliver to this connection and how they sign: the
 * endpoint URL, the secret's status (generated here and shown once) and
 * a signed curl example.
 */
export function IngressEndpointCard({ workspaceId, resourceId }: Props) {
  const qc = useQueryClient()
  const key = ['ingress', workspaceId, resourceId]
  const {
    data: info,
    error,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: key,
    queryFn: () => ingressApi.info(workspaceId, resourceId),
  })
  const showSkeleton = useSkeletonDelay(isPending)
  const [shown, setShown] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const rotate = useMutation({
    mutationFn: () => ingressApi.rotateSecret(workspaceId, resourceId),
    onSuccess: (r) => {
      setShown(r.secret)
      setConfirming(false)
      qc.setQueryData(key, r.info)
    },
    onError: () => setConfirming(false),
  })

  if (error instanceof ApiError && error.status === 400) return null
  if (info?.managed) return null
  if (isPending && !showSkeleton) return null

  const heading = (
    <SectionHeader rule trailingSpacing="compact">
      {ingressCopy.endpoint.heading}
    </SectionHeader>
  )
  if (isPending) {
    return (
      <Stack as="section" gap="space-12" aria-busy="true">
        {heading}
        <Skeleton lines={2} />
      </Stack>
    )
  }
  if (isError || !info) {
    return (
      <Stack as="section" gap="space-12">
        {heading}
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline">
            {ingressCopy.endpoint.loadFailed}
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {ingressCopy.retry}
            </Button>
          </Inline>
        </Alert>
      </Stack>
    )
  }

  const url = absolute(info.endpoint_url)
  const deliveryHeader = info.delivery_header
    ? ` \\\n  -H '${info.delivery_header}: <unique id>'`
    : ''
  const example =
    `BODY='{"hello":"world"}'\n` +
    `SIG=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$SECRET" | sed 's/^.* //')\n` +
    `curl -X POST '${url}' \\\n` +
    `  -H 'content-type: application/json' \\\n` +
    `  -H '${info.signature_header}: ${info.signature_prefix}'"$SIG"` +
    `${deliveryHeader} \\\n` +
    `  --data "$BODY"`

  return (
    <Stack as="section" gap="space-12">
      {heading}
      <CodeBlock caption={ingressCopy.endpoint.url} wrap maxLines={3}>
        {url}
      </CodeBlock>

      {rotate.isError ? (
        <Alert tone="danger">
          {ingressCopy.endpoint.generateFailed(
            rotate.error instanceof Error
              ? rotate.error.message
              : String(rotate.error),
          )}
        </Alert>
      ) : shown ? (
        <Stack gap="space-8">
          <Alert tone="success">{ingressCopy.endpoint.newSecret}</Alert>
          <CodeBlock caption={ingressCopy.endpoint.secret} wrap maxLines={3}>
            {shown}
          </CodeBlock>
        </Stack>
      ) : info.has_secret ? (
        <Text as="p" tone="text-sub">
          {ingressCopy.endpoint.secretSet}
        </Text>
      ) : (
        <Alert tone="warning">{ingressCopy.endpoint.noSecret}</Alert>
      )}

      <Inline gap="space-8">
        <Button
          variant="outline"
          loading={rotate.isPending}
          onClick={() =>
            info.has_secret ? setConfirming(true) : rotate.mutate()
          }
        >
          {info.has_secret
            ? ingressCopy.endpoint.replace
            : ingressCopy.endpoint.generate}
        </Button>
      </Inline>

      <Disclosure label={ingressCopy.endpoint.howToSign}>
        <Stack gap="space-12">
          <Facts>
            <Fact label={ingressCopy.endpoint.signatureHeader}>
              <Text variant="code">{info.signature_header}</Text>
            </Fact>
            <Fact label={ingressCopy.endpoint.signature}>
              {ingressCopy.endpoint.signatureValue(info.signature_prefix)}
            </Fact>
            {info.delivery_header && (
              <Fact label={ingressCopy.endpoint.deliveryHeader}>
                <Text variant="code">{info.delivery_header}</Text>{' '}
                {info.delivery_header_required
                  ? ingressCopy.endpoint.required
                  : ingressCopy.endpoint.optional}
                {' · '}
                {ingressCopy.endpoint.deliveryNote}
              </Fact>
            )}
          </Facts>
          <CodeBlock
            title={ingressCopy.endpoint.example}
            caption={ingressCopy.endpoint.exampleCaption}
            wrap
          >
            {example}
          </CodeBlock>
        </Stack>
      </Disclosure>

      <ConfirmDialog
        open={confirming}
        title={ingressCopy.endpoint.replaceTitle}
        description={ingressCopy.endpoint.replaceDescription}
        confirmLabel={ingressCopy.endpoint.replaceConfirm}
        tone="danger"
        loading={rotate.isPending}
        onConfirm={() => rotate.mutate()}
        onClose={() => setConfirming(false)}
      />
    </Stack>
  )
}
